<script lang="ts">
	import MetadataList from '#lib/MetadataList.svelte';
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import type { CompanySource } from '#src/types/sources.ts';

	interface Props {
		source: CompanySource;
	}
	let { source }: Props = $props();

	const coreMetadata = $derived.by(() => {
		const m: Record<string, unknown> = {};
		if (source.register_number) {
			m[t('sources.company.registerNumber')] = source.register_number;
		}
		if (source.legal_form) {
			m[t('sources.company.legalForm')] = source.legal_form;
		}
		if (source.active != null) {
			m[t('sources.company.active')] = source.active;
		}
		if (source.activity_type) {
			m[t('sources.company.activityType')] = source.activity_type;
		}
		if (source.employee_fte != null) {
			m[t('sources.company.employeeFte')] = source.employee_fte;
		}
		if (source.financial_expenses_eur) {
			m[t('sources.company.financialExpenses')] =
				`€${source.financial_expenses_eur.from.toLocaleString()}–${source.financial_expenses_eur.to.toLocaleString()}`;
		}
		if (source.members_count) {
			m[t('sources.company.members')] = source.members_count.as_of
				? `${source.members_count.total} (${source.members_count.as_of})`
				: source.members_count.total;
		}
		return m;
	});
</script>

{#if Object.keys(coreMetadata).length || source.activity_description}
	<div class="section">
		{#if source.activity_description}
			<p class="description">{source.activity_description}</p>
		{/if}
		{#if Object.keys(coreMetadata).length}
			<MetadataList metadata={coreMetadata} />
		{/if}
	</div>
{/if}

{#if source.codex_violations}
	<div class="section">
		<span class="badge warning">{t('sources.company.codexViolations')}</span>
	</div>
{/if}

{#if source.legal_representatives?.length}
	<div class="section">
		<h4>{t('sources.company.legalRepresentatives')}</h4>
		<ul class="compact-list">
			{#each source.legal_representatives as rep (rep)}
				<li>{rep}</li>
			{/each}
		</ul>
	</div>
{/if}

{#if source.entrusted_persons?.length}
	<div class="section">
		<h4>{t('sources.company.entrustedPersons')}</h4>
		<ul class="compact-list">
			{#each source.entrusted_persons as person (person)}
				<li>{person}</li>
			{/each}
		</ul>
	</div>
{/if}

{#if source.legislative_projects?.length}
	<div class="section">
		<h4>{t('sources.company.legislativeProjects')}</h4>
		<ul class="compact-list">
			{#each source.legislative_projects as proj (proj)}
				<li>{proj}</li>
			{/each}
		</ul>
	</div>
{/if}

{#if source.memberships?.length}
	<div class="section">
		<h4>{t('sources.company.memberships')}</h4>
		<ul class="compact-list">
			{#each source.memberships as m (m)}
				<li>{m}</li>
			{/each}
		</ul>
	</div>
{/if}

{#if source.client_organizations?.length}
	<div class="section">
		<h4>{t('sources.company.clientOrganizations')}</h4>
		<ul class="compact-list">
			{#each source.client_organizations as org (org)}
				<li>{org}</li>
			{/each}
		</ul>
	</div>
{/if}

{#if source.fields_of_interest?.length}
	<div class="section">
		<h4>{t('sources.company.fieldsOfInterest')}</h4>
		<div class="tags">
			{#each source.fields_of_interest as field (field)}
				<span class="tag">{field}</span>
			{/each}
		</div>
	</div>
{/if}

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.section {
		@include lib.flex-column();
		gap: 0.5rem;

		.description {
			font-size: 0.875rem;
			margin: 0;
			color: var(--text-secondary);
		}

		.badge {
			font-size: 0.75rem;
			padding: 0.125rem 0.5rem;
			border-radius: 999px;

			&.warning {
				background: var(--surface-warning);
				color: var(--text-warning);
			}
		}

		.tags {
			@include lib.flex-row();
			flex-wrap: wrap;
			gap: 0.25rem;

			.tag {
				font-size: 0.75rem;
				padding: 0.125rem 0.5rem;
				border-radius: 999px;
				background: var(--surface-3);
			}
		}

		.compact-list {
			margin: 0;
			padding-left: 1rem;
			font-size: 0.875rem;

			li {
				margin-bottom: 0.125rem;
			}
		}
	}
</style>
